'use client';

import Link from 'next/link';
import { redirect, useRouter } from 'next/navigation';
import { useActionState, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AuthForm } from '@/components/auth-form';
import { SubmitButton } from '@/components/submit-button';

import { login, type LoginActionState } from '../actions';
import { getLocalStorage, isEn } from '@/lib/utils';
import { useDeviceFingerprint } from '@/hooks/useDeviceFingerprint';
import { GithubSignInButton } from '@/components/ui-ai/github-signin-button';

export default function Page() {
  const router = useRouter();
  const { deviceId, isLoading } = useDeviceFingerprint();

  const [email, setEmail] = useState('');
  const [isSuccessful, setIsSuccessful] = useState(false);

  const [state, formAction] = useActionState<LoginActionState, FormData>(
    login,
    {
      status: 'idle',
    },
  );

  useEffect(() => {
    const email =  getLocalStorage('email');
    setEmail(email);

    if (state.status === 'failed') {
      toast.error(isEn ? 'Invalid credentials!' : '登录凭证无效');
    } else if (state.status === 'invalid_data') {
      toast.error(isEn ? 'Failed validating your submission!' : '登录验证失败');
    } else if (state.status === 'success') {
      setIsSuccessful(true);
      // 显示成功消息
      toast.success(isEn ? 'Login successful! Redirecting...' : '登录成功！正在跳转...');
      
      // 延迟1秒后跳转，让用户看到成功消息
      const timer = setTimeout(() => {
        // 替换历史记录，防止用户返回登录页
        router.replace('/');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [state.status, router]);

  const handleSubmit = (formData: FormData) => {
    setEmail(formData.get('email') as string);
    formAction(formData);
  };

  return (
    <div className="flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-3xl font-bold">登录</h1>
          <p className="text-muted-foreground mt-2">
            选择您的登录方式
          </p>
        </div>
        <div className="space-y-4">
          {/* GitHub 登录按钮 */}
          <GithubSignInButton />
          {/* 分隔线 */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                或者
              </span>
            </div>
          </div>

          <div className="w-full max-w-md overflow-hidden rounded-2xl flex flex-col gap-12">
            <div className="flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16">
              <h3 className="text-xl font-semibold dark:text-zinc-50">{isEn? 'Login' :'登 录'}</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-400">
                {isEn ? 'Use your email and password to sign in' : '请输入邮箱和密码'}
              </p>
            </div>
            <AuthForm action={handleSubmit} defaultEmail={email} defaultId={deviceId || undefined}>
              <SubmitButton isSuccessful={isSuccessful}>{isEn? 'Login' :'登 录'}</SubmitButton>
              <p className="text-center text-sm text-gray-600 mt-4 dark:text-zinc-400">
                {isEn ? "Don't have an account? ": "还没有账号？"}
                <Link
                  href="/register"
                  className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
                >
                  {isEn ? 'Sign up' : '去 注 册'}
                </Link>
                {isEn ? " for free. ": ""}
              </p>
            </AuthForm>
          </div>
        </div>
    </div>
  );
}
